import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import type { CleanupGroup } from '@/bindings/CleanupGroup'
import type { TableDeleted } from '@/bindings/TableDeleted'
import type { TablePreview } from '@/bindings/TablePreview'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { Field, Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { CLEANUP_GROUPS, previewCleanup, runCleanup, toCutoff } from '@/features/cleanup/api'
import { PreviewTable, TableName } from '@/features/cleanup/preview-table'
import { useT } from '@/lib/i18n/translate'
import { todayIn } from '@/lib/time'

/**
 * Data cleanup (H7): platform admins pick a cutoff per group, *check* what
 * would go, then *clean* it. Nothing is deleted without a check for the very
 * value the button runs with — editing the date drops the check.
 */
export function CleanupPage() {
  const t = useT()
  return (
    <Page width="lg" title={t('cleanup.title')} className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t('cleanup.intro')}</p>
      <p className="text-muted text-sm">{t('cleanup.vacuumNote')}</p>
      {CLEANUP_GROUPS.map((group) => (
        <GroupCard key={group} group={group} />
      ))}
    </Page>
  )
}

interface Checked {
  before: string
  tables: TablePreview[]
}

function GroupCard({ group }: { group: CleanupGroup }) {
  const t = useT()
  const [local, setLocal] = useState('')
  const [checked, setChecked] = useState<Checked | null>(null)
  const [progress, setProgress] = useState<TableDeleted[] | null>(null)
  const [finished, setFinished] = useState<TableDeleted[] | null>(null)
  const before = toCutoff(local)

  const check = useMutation({
    mutationFn: (before: string) => previewCleanup({ group, before }),
    onSuccess: (preview, before) => setChecked({ before, tables: preview.tables }),
  })
  const clean = useMutation({
    mutationFn: (before: string) => runCleanup({ group, before }, setProgress),
    onSuccess: (totals) => {
      setFinished(totals)
      setChecked(null)
    },
    onSettled: () => setProgress(null),
  })

  // Only a check for this exact cutoff, with rows to remove, arms the button.
  const armed = checked !== null && checked.before === before && checked.tables.some((table) => table.rows > 0)

  function changeDate(value: string) {
    setLocal(value)
    setChecked(null)
    setFinished(null)
    check.reset()
    clean.reset()
  }

  const dateLabel = t('cleanup.before')
  const groupLabel = t(`cleanup.group.${group}`)

  return (
    <Card aria-labelledby={`cleanup-${group}`} className="flex flex-col gap-3">
      <h2 id={`cleanup-${group}`} className="text-lg font-semibold">
        {groupLabel}
      </h2>
      <p className="text-muted text-sm">{t(`cleanup.describe.${group}`)}</p>
      {group === 'orders' && (
        <p role="note" className="text-warning text-sm">
          {t('cleanup.ordersWarning')}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field label={dateLabel} className="w-44">
          <Input
            type="date"
            value={local}
            max={todayIn(undefined)}
            aria-label={`${dateLabel}: ${groupLabel}`}
            onChange={(e) => changeDate(e.target.value)}
          />
        </Field>
        <Button
          variant="secondary"
          disabled={before === undefined || clean.isPending}
          loading={check.isPending}
          aria-label={`${t('cleanup.check')}: ${groupLabel}`}
          onClick={() => before && check.mutate(before)}
        >
          {t('cleanup.check')}
        </Button>
      </div>
      {local !== '' && before === undefined && (
        <p role="alert" className="text-danger text-sm">
          {t('cleanup.mustBePast')}
        </p>
      )}
      <FormError error={check.error} />

      {checked && checked.before === before && (
        <div className="flex flex-col gap-3">
          {checked.tables.every((table) => table.rows === 0) ? (
            <p className="text-muted text-sm">{t('cleanup.nothing')}</p>
          ) : (
            <PreviewTable tables={checked.tables} />
          )}
          {armed && (
            <ConfirmButton
              label={t('cleanup.run')}
              ariaLabel={`${t('cleanup.run')}: ${groupLabel}`}
              variant="danger"
              size="md"
              confirmLabel={t('cleanup.confirm')}
              confirmAriaLabel={`${t('cleanup.confirm')}: ${groupLabel}`}
              note={
                <>
                  <p className="text-sm">
                    {t('cleanup.confirmText', {
                      rows: checked.tables.reduce((sum, table) => sum + table.rows, 0),
                      before: new Date(checked.before).toLocaleDateString(),
                    })}
                  </p>
                  {progress && (
                    <p role="status" className="text-sm">
                      {t('cleanup.progress', { rows: progress.reduce((sum, table) => sum + table.rows, 0) })}
                    </p>
                  )}
                </>
              }
              confirm={() => clean.mutateAsync(checked.before)}
            />
          )}
        </div>
      )}
      <FormError error={clean.error} />

      {finished && (
        <div role="status" className="flex flex-col gap-2">
          <p className="font-medium">{t('cleanup.finished')}</p>
          <DeletedList tables={finished} />
        </div>
      )}
    </Card>
  )
}

function DeletedList({ tables }: { tables: TableDeleted[] }) {
  return (
    <ul className="text-sm">
      {tables.map((table) => (
        <li key={table.table}>
          <TableName table={table.table} />: <span className="tabular-nums">{table.rows.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  )
}
